import {
  createContext,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useParams } from "react-router-dom"

import { DataUploadSteps, IDataUpload, IReportObs } from "../interfaces/markets"
import {
  useDeleteMSMutation,
  useLazyGetMSByIdQuery,
  useLazyGetMSExcelAsJsonQuery,
  useSaveMSDetailsMutation,
  useSaveMSExcelMutation,
} from "../services/marketprices/mappingSchemas"
import {
  useDeleteReportMutation,
  useGetReportByIdQuery,
  usePostReportMutation,
  useSaveReportMutation,
} from "../services/marketprices/reports"
import { MappingSchemaStatus } from "../constants/prices"

export interface IDataUploadContext {
  completedStep: number
  setCompletedStep: (value: number) => void
  filters: IDataUpload
  setFilters: Dispatch<SetStateAction<IDataUpload>>
  showNewMapping: boolean
  setShowNewMapping: Dispatch<SetStateAction<boolean>>
  newMapping: { name: string; id: number; mappingSchemaJSON: Record<string, string>[]; isCompleted: boolean } | null
  isLoading: boolean
  setNewMapping: Dispatch<
    SetStateAction<{
      name: string
      id: number
      mappingSchemaJSON: Record<string, string>[]
      isCompleted: boolean
    } | null>
  >
  handleNewMapping: () => Promise<void>
  handleNextMapping: () => Promise<void>
  resetNewMapping: () => Promise<void>
  submitReport: () => Promise<void>
  disabledNext: boolean
  setDisabledNext: Dispatch<SetStateAction<boolean>>
  obsWithOutliers: IReportObs[]
  setObsWithOutliers: Dispatch<SetStateAction<IReportObs[]>>
}

export const initialFilters: IDataUpload = {
  adm0Code: 0,
  file: null,
  note: "",
  mappingSchemaId: 0,
  mappingSchemaName: "",
  reportId: 0,
}

const initialDataUploadContext: IDataUploadContext = {
  completedStep: 0,
  showNewMapping: false,
  setShowNewMapping: () => {},
  setCompletedStep: () => {},
  filters: initialFilters,
  setFilters: () => {},
  newMapping: null,
  setNewMapping: () => {},
  handleNewMapping: async () => {},
  handleNextMapping: async () => {},
  resetNewMapping: async () => {},
  isLoading: false,
  submitReport: async () => {},
  disabledNext: false,
  setDisabledNext: () => {},
  obsWithOutliers: [],
  setObsWithOutliers: () => {},
}

export const DataUploadContext = createContext<IDataUploadContext>(initialDataUploadContext)

const DataUploadProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const { rId } = useParams<{ rId: string }>()
  const initialCompletedStep = Number(rId) ? DataUploadSteps.MAPPING : rId ? DataUploadSteps.DATA : 0
  const [completedStep, setCompletedStep] = useState<number>(initialCompletedStep)
  const [disabledNext, setDisabledNext] = useState<boolean>(false)
  const [filters, setFilters] = useState<IDataUpload>(initialFilters)
  const [showNewMapping, setShowNewMapping] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [newMapping, setNewMapping] = useState<{
    id: number
    name: string
    mappingSchemaJSON: Record<string, string>[]
    isCompleted: boolean
  } | null>(null)
  const [obsWithOutliers, setObsWithOutliers] = useState<IReportObs[]>([])
  const newMappingRef = useRef(newMapping)

  const [saveMappingSchema] = useSaveMSDetailsMutation()
  const [saveMSExcel] = useSaveMSExcelMutation()
  const [getMSExcelAsJson] = useLazyGetMSExcelAsJsonQuery()
  const [saveReport] = useSaveReportMutation()
  const [postReport] = usePostReportMutation()
  const [deleteMS] = useDeleteMSMutation()
  const [deleteReport] = useDeleteReportMutation()

  const { data: reportData } = useGetReportByIdQuery(Number(rId), { skip: !rId })
  const [getMSData] = useLazyGetMSByIdQuery()

  const resetNewMapping = async (): Promise<void> => {
    if (newMapping?.id && !newMapping?.isCompleted) {
      await deleteMS(newMapping.id).unwrap()
      setNewMapping(null)
    }
  }

  const submitReport = async (): Promise<void> => {
    if (!filters.file || !filters.adm0Code || !filters.mappingSchemaId || !filters.reportId) return
    try {
      setIsLoading(true)
      const formData = new FormData()
      formData.append("files", filters.file)

      await postReport({
        cid: filters.adm0Code,
        msid: filters.mappingSchemaId,
        rid: filters.reportId,
        file: formData,
      }).unwrap()
      setDisabledNext(false)
    } catch (error) {
      const err = error as { data?: string }
      throw new Error(err?.data ?? "")
    } finally {
      setIsLoading(false)
    }
  }

  const handleNewMapping = async (): Promise<void> => {
    if (!filters.file) return

    setIsLoading(true)
    await resetNewMapping()

    const formData = new FormData()
    formData.append("files", filters.file)

    const response = await saveMappingSchema({
      adm0code: filters.adm0Code,
      mappingSchemaName: filters.mappingSchemaName ?? "",
      mappingSchemaTypeId: 1,
    }).unwrap()
    const result = await saveMSExcel(formData).unwrap()
    const json = await getMSExcelAsJson({ fileName: result.fileName, msId: response.id }).unwrap()
    const msData = await getMSData(response.id).unwrap()

    setNewMapping({
      id: response.id,
      name: filters.mappingSchemaName ?? "",
      mappingSchemaJSON: json,
      isCompleted: msData?.mappingSchemaStatusId === MappingSchemaStatus.Complete,
    })
    setIsLoading(false)
  }

  const handleNextMapping = async (): Promise<void> => {
    if (!filters.mappingSchemaName && (!filters.mappingSchemaId || !filters.file))
      throw new Error("Mapping schema id or file is missing")

    setIsLoading(true)
    let reportId = 0

    const formData = new FormData()
    if (filters.file) formData.append("files", filters.file)

    try {
      if (filters.mappingSchemaId || newMapping?.id) {
        const response = await saveReport({
          adm0Code: filters.adm0Code,
          mappingSchemaId: filters.mappingSchemaId || newMapping?.id || 0,
          reportComment: filters.note ?? "",
        }).unwrap()
        reportId = response.reportID
        setFilters(prev => ({ ...prev, reportId }))

        await postReport({
          cid: filters.adm0Code,
          msid: filters.mappingSchemaId || newMapping?.id || 0,
          rid: reportId,
          file: formData,
        }).unwrap()
      }
    } catch (error) {
      if (reportId) await deleteReport(reportId).unwrap()
      setFilters(prev => ({ ...prev, reportId: 0 }))
      const err = error as { data?: string | { detail: string } }
      if (typeof err?.data === "string") throw new Error(err.data)
      throw new Error(err?.data?.detail ?? "")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    return () => {
      const mapping = newMappingRef.current
      if (mapping?.id && !mapping?.isCompleted) {
        deleteMS(mapping.id)
      }
    }
  }, [])

  useEffect(() => {
    newMappingRef.current = newMapping
  }, [newMapping])

  useEffect(() => {
    if (reportData) {
      setFilters(prev => ({
        ...prev,
        mappingSchemaId: reportData.mappingSchemaId,
        adm0Code: reportData.adm0Code,
        reportId: reportData.reportId,
      }))
    }
  }, [reportData])

  const value = useMemo(
    () => ({
      completedStep,
      setCompletedStep,
      filters,
      setFilters,
      showNewMapping,
      setShowNewMapping,
      newMapping,
      setNewMapping,
      handleNewMapping,
      handleNextMapping,
      resetNewMapping,
      isLoading,
      submitReport,
      disabledNext,
      setDisabledNext,
      obsWithOutliers,
      setObsWithOutliers,
    }),
    [
      completedStep,
      filters,
      newMapping,
      showNewMapping,
      isLoading,
      handleNewMapping,
      handleNextMapping,
      resetNewMapping,
      submitReport,
      disabledNext,
      obsWithOutliers,
    ],
  )

  return <DataUploadContext.Provider value={value}>{children}</DataUploadContext.Provider>
}

export default DataUploadProvider
